/**
 * Pure helpers for column mapping (promedhis-style).
 * Mapping rows use dtoProperty as the stable id for device DTO imports,
 * and contractKey as the stable id for patient bulk imports.
 */

import {
  PATIENT_IMPORT_FIELD_CATALOG,
  PATIENT_REQUIRED_CONTRACT_KEYS,
} from './importFieldCatalog';

export const PATIENT_REQUIRED_FIELDS = PATIENT_REQUIRED_CONTRACT_KEYS;

export function normalizeHeaderKey(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

export function extractTrailingGuid(header) {
  if (!header) return null;
  const parts = String(header).split('_');
  const last = parts[parts.length - 1]?.trim();
  if (!last || last.length < 32) return null;
  return /^[0-9a-fA-F-]{32,36}$/.test(last) ? last.toLowerCase() : null;
}

export function findBestHeaderMatch(targetField, fileHeaders, sourceToTarget = {}) {
  const headers = (fileHeaders || []).map((h) => String(h).trim()).filter(Boolean);
  if (!headers.length || !targetField) return '';

  const exact = headers.find((h) => h === targetField);
  if (exact) return exact;

  const ci = headers.find((h) => h.toLowerCase() === targetField.toLowerCase());
  if (ci) return ci;

  const defaultSources = Object.entries(sourceToTarget || {})
    .filter(([, target]) => String(target).trim() === targetField)
    .map(([source]) => source);

  for (const source of defaultSources) {
    const hit = headers.find((h) => h === source);
    if (hit) return hit;
  }

  for (const source of defaultSources) {
    const guid = extractTrailingGuid(source);
    if (!guid) continue;
    const hit = headers.find((h) => extractTrailingGuid(h) === guid);
    if (hit) return hit;
  }

  const targetNorm = normalizeHeaderKey(targetField);
  const fuzzy = headers.find((h) => normalizeHeaderKey(h) === targetNorm);
  if (fuzzy) return fuzzy;

  // Also try matching DTO-style names against headers
  const fuzzyPartial = headers.find((h) => normalizeHeaderKey(h).includes(targetNorm) || targetNorm.includes(normalizeHeaderKey(h)));
  if (fuzzyPartial && targetNorm.length > 3) return fuzzyPartial;

  return '';
}

/**
 * Device / generic DTO mappings: property names are DTO columns.
 * itemDto: { propName: 'string'|'DateTime'|... }
 */
export function buildDtoAutoMappings(itemDto, fileHeaders) {
  const props = itemDto?.data ? Object.keys(itemDto.data) : Object.keys(itemDto || {});
  const typeOf = (p) => (itemDto?.data ? itemDto.data[p] : itemDto?.[p]) || '';
  return props
    .filter((p) => p !== 'id' && p !== 'Id')
    .map((dtoProperty) => ({
      key: dtoProperty,
      dtoProperty,
      contractKey: dtoProperty,
      type: typeOf(dtoProperty),
      entity: 'Implant',
      csvHeader: findBestHeaderMatch(dtoProperty, fileHeaders, {}),
      defaultValue: typeOf(dtoProperty) === 'DateTime' ? new Date().toISOString() : '',
    }));
}

/**
 * Patient+devices bulk: rows keyed by contractKey, labelled with PatientDto/ImplantInstanceDto names.
 */
export function buildPatientAutoMappings(fileHeaders, sourceToTarget = {}, catalog = PATIENT_IMPORT_FIELD_CATALOG) {
  return catalog.map((field) => ({
    key: field.contractKey,
    dtoProperty: field.dtoProperty,
    contractKey: field.contractKey,
    type: field.type,
    entity: field.entity,
    required: field.required,
    csvHeader: findBestHeaderMatch(field.contractKey, fileHeaders, sourceToTarget),
    defaultValue: '',
  }));
}

/** @deprecated use buildPatientAutoMappings / buildDtoAutoMappings */
export function buildAutoMappings(targetFields, fileHeaders, sourceToTarget = {}) {
  return (targetFields || []).map((dtoProperty) => ({
    key: dtoProperty,
    dtoProperty,
    contractKey: dtoProperty,
    csvHeader: findBestHeaderMatch(dtoProperty, fileHeaders, sourceToTarget),
    defaultValue: '',
  }));
}

/** Patient bulk API: source header → contract key */
export function mappingsToSourceToTarget(mappings) {
  const out = {};
  (mappings || []).forEach((m) => {
    const source = (m.csvHeader || '').trim();
    const target = (m.contractKey || m.dtoProperty || '').trim();
    if (!source || !target) return;
    out[source] = target;
  });
  return out;
}

export function validateRequiredMappings(mappings, requiredFields = PATIENT_REQUIRED_CONTRACT_KEYS) {
  const missing = [];
  (requiredFields || []).forEach((field) => {
    const row = (mappings || []).find(
      (m) => m.contractKey === field || m.dtoProperty === field || m.key === field
    );
    if (!row || !String(row.csvHeader || '').trim()) {
      missing.push(field);
    }
  });
  return missing;
}
